import { ICrudLocale } from '@app/i18n/interface';

/**
 * Textos del catálogo (M-08, V-A09, 2026-10-30): en español y sin jerga de desarrollo
 * («run», «cron», «marca de agua»), cajas de ayuda cortas y un solo estado por ejecución.
 */
export const CATALOG_SYNC_MANAGEMENT_LOCALE_ES: ICrudLocale = {
  CATALOG_DESTINATION: {
    LABEL: {
      SINGULAR: 'Destino del catálogo',
      PLURAL: 'Destinos del catálogo',
    },
    TITLE: 'Destinos que se sincronizan',
    SUBTITLE: 'Editar esta lista no gasta cuota de Hotelbeds.',
    EMPTY: 'Todavía no hay destinos en la lista.',
    FIELD: {
      CODE: 'Código',
      NAME: 'Nombre',
      ACTIVE: 'Se sincroniza',
      ORDER: 'Orden',
    },
    SOURCE: {
      DATABASE: 'La sincronización usa esta lista',
      DATABASE_DESC: 'Se sincronizan los destinos activos, en este orden.',
      ENVIRONMENT: 'Todavía manda el servidor',
      ENVIRONMENT_DESC:
        'Por ahora se sincronizan los destinos configurados en el servidor ({{codes}}). En cuanto actives uno aquí, mandará esta lista.',
      ENVIRONMENT_FULL_DESC:
        'Por ahora se sincroniza todo el catálogo de Hotelbeds, porque el servidor no limita los destinos. En cuanto actives uno aquí, mandará esta lista.',
    },
    IMPORT: {
      DESC: 'En el servidor están configurados {{codes}} y aquí todavía no.',
      ACTION: 'Traer los del servidor',
    },
    ACTION: {
      ADD: 'Añadir destino',
      REMOVE: 'Quitar',
      MOVE_UP: 'Subir {{code}} en el orden',
      MOVE_DOWN: 'Bajar {{code}} en el orden',
    },
    CONFIRM_REMOVE: {
      TITLE: '¿Quitar {{code}} de la lista?',
      DESC: 'Sus hoteles no se borran: siguen en la web, pero dejan de actualizarse. Si solo quieres pausarlo, desactívalo.',
      CONFIRM: 'Quitar {{code}}',
      CANCEL: 'Mantenerlo',
    },
    HINT: {
      ORDER:
        'El orden importa: cada sincronización tiene un tope de peticiones y se gasta de arriba abajo.',
      REMOVE:
        'Quitar un destino no borra sus hoteles, pero deja de actualizarlos.',
    },
    VALIDATION: {
      CODE_PATTERN: 'De 2 a 8 letras o números, sin espacios (MAD, MIA).',
    },
    ERROR: {
      SAVE: 'No pudimos guardar el cambio. Vuelve a intentarlo.',
    },
  },
  CATALOG_SYNC: {
    LABEL: { SINGULAR: 'Catálogo', PLURAL: 'Catálogo' },
    FIELD: {},
    TITLE: 'Catálogo',
    SUBTITLE:
      'Contenido de Hotelbeds guardado en IBSOA: hoteles, descripciones e imágenes. La web lee esta copia; no consulta a Hotelbeds en cada búsqueda.',
    EMPTY: 'Todavía no se ha sincronizado nunca.',
    STORED: {
      TITLE: 'Lo que hay guardado',
      COMPUTED_AT: 'Calculado el {{date}}',
      NOT_COMPUTED:
        'Sin calcular aún. Se calcula al terminar cada sincronización de hoteles y al medir la cobertura; abrir esta página no recorre el catálogo.',
      UNMAINTAINED:
        'Además hay {{hotels}} hoteles guardados de otros {{destinations}} destinos que no se sincronizan: siguen en la web, pero no se actualizan.',
    },
    COUNTS: {
      HOTELS: 'Hoteles',
      DESTINATIONS: 'Destinos',
      COUNTRIES: 'Países',
      CHAINS: 'Cadenas',
      WITH_IMAGES: 'Hoteles con imagen',
      ACCOMMODATION_TYPES: 'Tipos de alojamiento',
      BOARD_TYPES: 'Regímenes',
      FACILITY_TYPES: 'Instalaciones',
      MANAGED: 'Destinos de la lista que se sincronizan: {{count}}.',
      RETIRED: 'Dados de baja, porque Hotelbeds ya no los tiene: {{count}}.',
      TRANSLATIONS: 'Fichas traducidas: {{count}}.',
      UNAVAILABLE: 'No pudimos cargar los recuentos. Vuelve a intentarlo.',
    },
    COVERAGE: {
      TITLE: 'Cobertura del mapeo',
      THRESHOLDS:
        'Mínimos de Hotelbeds: {{gold}} % para los hoteles G&D (Gold & Diamond) y {{regular}} % para el resto.',
      IDENTIFIABLE: {
        TITLE: 'Hoteles identificables',
        DESC: 'Hoteles activos con nombre, dirección, ciudad y país: los que Hotelbeds puede cruzar con su catálogo. Se calcula en segundo plano, al terminar cada sincronización de hoteles y al medir la cobertura.',
      },
      PROVIDER: {
        TITLE: 'Cobertura frente a Hotelbeds',
        DESC: 'De los hoteles que Hotelbeds tiene en los destinos que vendemos, cuántos tenemos activos. Se mide con el trabajo «Cobertura frente a Hotelbeds».',
      },
      VERDICT: {
        MEETS_GOLD_DIAMOND: 'Cumple los dos mínimos',
        MEETS_REGULAR: 'Cumple el 90 %, no el 95 %',
        BELOW_REGULAR: 'Por debajo del mínimo',
        NOT_MEASURED: 'Sin medir',
      },
      NOT_MEASURED: {
        identifiable: 'No hay hoteles activos que medir.',
        provider:
          'Todavía no se ha medido. Lánzalo con «Ejecutar ahora» en «Cobertura frente a Hotelbeds».',
      },
      MATCHED: '{{matched}} de {{total}} hoteles.',
      BELOW: 'Por debajo del {{gold}} %: {{list}}.',
      EXTRA:
        'Nuestros que Hotelbeds ya no tiene: {{count}}. La próxima pasada completa los dará de baja.',
      MEASURED_AT: 'Calculado el {{date}}.',
      NOT_COMPUTED:
        'Sin calcular aún. Se calcula al terminar la próxima sincronización de hoteles o al lanzar «Cobertura frente a Hotelbeds».',
      SCOPE: 'Destinos medidos: {{list}}.',
      SCOPE_FULL: 'Se mide el catálogo completo.',
      GD_NOTE:
        'Hotelbeds no nos dice qué hoteles son G&D, así que cada cifra se compara con los dos mínimos.',
      UNAVAILABLE: 'No pudimos cargar la cobertura. Vuelve a intentarlo.',
    },
    JOB: {
      HOTEL_CATALOG: {
        NAME: 'Hoteles y contenido',
        DESC: 'Hoteles, descripciones, imágenes e instalaciones de los destinos de la lista.',
      },
      DICTIONARIES: {
        NAME: 'Diccionarios',
        DESC: 'Nombres de instalaciones, regímenes y tipos de alojamiento. Cuesta pocas peticiones.',
      },
      COVERAGE: {
        NAME: 'Cobertura frente a Hotelbeds',
        DESC: 'Compara la lista de hoteles de Hotelbeds con la nuestra. Solo lee: no cambia el catálogo.',
      },
    },
    JOBS: {
      TITLE: 'Trabajos de sincronización',
      SUBTITLE:
        'Cada trabajo se puede lanzar a mano. Lo que no se programa solo, solo corre cuando lo lanzas.',
      NEXT: 'Se ejecuta solo; la próxima vez, el {{date}}.',
      SCHEDULED: 'Se ejecuta solo cada día.',
      RUNS_WITH: 'Se ejecuta también al empezar «{{job}}».',
      MANUAL_ONLY: 'Solo cuando lo lanzas.',
      NEVER: 'Todavía no se ha ejecutado.',
      PROCESSED: 'procesados: {{count}}',
      RUN_NOW_LABEL: 'Ejecutar ahora: {{job}}',
      BLOCK: {
        RUNNING: 'Se está ejecutando.',
        COOLDOWN: 'Se podrá volver a lanzar a partir del {{date}}.',
      },
      LAUNCHED: {
        TITLE: 'Lanzado: {{job}}',
        DESC: 'Se ejecuta en segundo plano. Esta página se actualiza sola hasta que termine.',
      },
      ERROR: {
        COOLDOWN:
          'Este trabajo se lanzó hace muy poco. Espera a la hora que indica antes de repetirlo.',
        ALREADY_RUNNING: 'Este trabajo ya se está ejecutando.',
        GENERIC: 'No pudimos lanzar el trabajo. Vuelve a intentarlo.',
      },
      UNAVAILABLE: 'No pudimos cargar los trabajos. Vuelve a intentarlo.',
      NOTE: 'Cada ejecución tiene un único estado. Si falla, el motivo aparece en el historial y la siguiente se programa sola. Durante una reserva no se consulta el contenido a Hotelbeds.',
    },
    HISTORY: {
      TITLE: 'Historial de sincronizaciones',
      SUBTITLE: 'Las más recientes primero. Horas en tu hora local.',
      FILTER: 'Filtrar el historial',
      JOB: 'Trabajo',
      ALL_JOBS: 'Todos los trabajos',
      STARTED: 'Inicio',
      DURATION: 'Duración',
      PROCESSED: 'Procesados',
      REQUESTS: 'Peticiones',
      RESULT: 'Resultado',
      BY: 'Manual, {{name}}',
      NO_DATA: 'Todavía no hay ejecuciones registradas.',
      NO_DATA_FILTER: 'Este trabajo todavía no se ha ejecutado.',
    },
    RUN_STATUS: {
      RUNNING: 'En curso',
      SUCCESS: 'Correcta',
      PARTIAL: 'Con fallos',
      FAILED: 'Fallida',
    },
    TRIGGER_SHORT: { SCHEDULED: 'Programada', MANUAL: 'Manual' },
    RUN: {
      TITLE: 'Sincronización de hoteles: última ejecución',
      STATE: 'Estado',
      STARTED_AT: 'Empezó',
      DURATION: 'Duración',
      DURATION_VALUE: '{{minutes}} min {{seconds}} s',
      RESULT: 'Resultado',
      RESULT_VALUE:
        '{{hotels}} hoteles actualizados; {{ok}} destinos correctos y {{failed}} con fallo.',
      LAST_SUCCESS: 'Última sincronización correcta',
      NEVER: 'Nunca',
      NEXT: 'Próxima sincronización',
      NEXT_INCREMENTAL: 'Solo los cambios desde el {{date}}',
      NEXT_FULL: 'Completa: todo el contenido de los destinos',
      DESTINATIONS: 'Destinos incluidos',
      FULL_CATALOG: 'Todo el catálogo de Hotelbeds',
      ERRORS: 'Por qué no salió bien',
      NOTES: 'Notas de esta sincronización',
      TECHNICAL: 'Ver el mensaje técnico de Hotelbeds',
      NOTE: 'Se sincroniza sola cada día y actualiza los hoteles, descripciones e imágenes de los destinos de la lista. Horas en tu hora local.',
    },
    STATE: {
      IDLE: 'Nunca ejecutada',
      RUNNING: 'En curso',
      SUCCESS: 'Correcta',
      PARTIAL: 'Con fallos',
      FAILED: 'Fallida',
    },
    STATE_DESC: {
      IDLE: 'Todavía no se ha ejecutado nunca.',
      RUNNING: 'Se está ejecutando ahora.',
      SUCCESS: 'Terminó sin errores,',
      PARTIAL: 'Terminó, pero algunos destinos fallaron,',
      FAILED: 'No terminó,',
    },
    TRIGGER: {
      SCHEDULED: 'lanzada automáticamente.',
      MANUAL: 'lanzada a mano.',
    },
    ACTION: {
      TRIGGER: 'Ejecutar ahora',
      TRIGGERING: 'Lanzando…',
    },
    COST: {
      DESC: 'Cada ejecución gasta peticiones de la cuota diaria de Hotelbeds (50.000 al día para todo el equipo). Para no repetirla sin querer, un mismo trabajo no se puede volver a lanzar hasta pasado un rato.',
    },
    POLL_TIMEOUT: {
      DESC: 'La sincronización sigue en curso. Vuelve más tarde para ver cómo terminó.',
    },
    ERROR: {
      TITLE: 'No se pudo completar la acción',
      LOAD: 'No pudimos cargar el estado del catálogo. Inténtalo de nuevo.',
    },
  },
};
